import { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaSync, FaPlus, FaTrash, FaTag } from "react-icons/fa";
import {
  getAllPromoCodes,
  deletePromoCode,
} from "../../../store/slices/admin/asyncThunks";
import {
  selectPromoCodes,
  selectPromoCodesPagination,
  selectPromoCodesLoading,
  selectPromoCodeFilters,
  setPromoCodeFilters,
} from "../../../store/slices/admin/slice";
import { useAdminModal } from "../../../contexts/AdminModalContext";
import {
  SectionHeader,
  SectionTitle,
  SectionActions,
  Button,
  SearchContainer,
  SearchInput,
  LoadingContainer,
  DataTableScroll,
  DataTable,
  TableHeader,
  TableRow,
  TableCell,
  CourseTags,
  CourseTag,
  StatusBadge,
  EmptyMessage,
  PaginationContainer,
  PageInfo,
  CourseIcon,
  UserInfo,
  CourseTitle,
  CourseInfo,
  CourseCategory,
} from "./AdminPromoCodes.styles";

const AdminPromoCodes = () => {
  const dispatch = useDispatch();
  const promoCodes = useSelector(selectPromoCodes);
  const pagination = useSelector(selectPromoCodesPagination);
  const loading = useSelector(selectPromoCodesLoading);
  const filters = useSelector(selectPromoCodeFilters);
  const { openCreatePromoCodeModal, showConfirmModal } = useAdminModal();
  const searchTimeoutRef = useRef(null);

  const fetchPromoCodes = useCallback(() => {
    dispatch(getAllPromoCodes(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    fetchPromoCodes();
  }, [fetchPromoCodes]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      dispatch(setPromoCodeFilters({ search: value, page: 1 }));
    }, 500);
  };

  const handlePageChange = (newPage) => {
    dispatch(setPromoCodeFilters({ page: newPage }));
  };

  const handleOpenCreateModal = () => {
    openCreatePromoCodeModal(fetchPromoCodes);
  };

  const handleDelete = (promoCode) => {
    showConfirmModal(
      "Brisanje promo koda",
      `Da li ste sigurni da zelite da obrisete promo kod "${promoCode.code}"?`,
      async () => {
        await dispatch(deletePromoCode(promoCode.id)).unwrap();
        fetchPromoCodes();
      },
    );
  };

  return (
    <>
      <SectionHeader>
        <SectionTitle>Promo Kodovi</SectionTitle>
        <SectionActions>
          <Button $primary onClick={handleOpenCreateModal}>
            <FaPlus /> Dodaj Promo Kod
          </Button>
          <Button
            $secondary
            onClick={fetchPromoCodes}
            disabled={loading}
            $loading={loading}
          >
            <FaSync /> Osvezi
          </Button>
        </SectionActions>
      </SectionHeader>

      <SearchContainer>
        <SearchInput
          placeholder="Pretrazi promo kodove..."
          defaultValue={filters.search}
          onChange={handleSearchChange}
        />
      </SearchContainer>

      {loading ? (
        <LoadingContainer>
          <FaSync
            className="spin"
            style={{ fontSize: 24, color: "var(--accent)" }}
          />
        </LoadingContainer>
      ) : (
        <>
          <DataTableScroll>
            <DataTable>
              <TableHeader>Promo Kod</TableHeader>
              <TableHeader $align="center">Popust</TableHeader>
              <TableHeader>Kursevi</TableHeader>
              <TableHeader $align="center">Akcije</TableHeader>

              {promoCodes && promoCodes.length > 0 ? (
                promoCodes.map((pc) => (
                  <TableRow key={pc.id}>
                    <TableCell>
                      <UserInfo>
                        <CourseIcon>
                          <FaTag />
                        </CourseIcon>
                        <CourseInfo>
                          <CourseTitle>{pc.code}</CourseTitle>
                          <CourseCategory>{pc.name}</CourseCategory>
                        </CourseInfo>
                      </UserInfo>
                    </TableCell>
                    <TableCell
                      $align="center"
                      style={{ justifyContent: "center" }}
                    >
                      <StatusBadge $active>
                        {Math.round(parseFloat(pc.percentage) * 100)}%
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      <CourseTags>
                        {pc.courses && pc.courses.length > 0 ? (
                          pc.courses.map((course) => (
                            <CourseTag key={course.id}>
                              {course.title}
                            </CourseTag>
                          ))
                        ) : (
                          <span
                            style={{ color: "var(--text-muted)", fontSize: 12 }}
                          >
                            Nema kurseva
                          </span>
                        )}
                      </CourseTags>
                    </TableCell>
                    <TableCell
                      $align="center"
                      style={{ justifyContent: "center" }}
                    >
                      <Button
                        $danger
                        $small
                        onClick={() => handleDelete(pc)}
                        title="Obrisi"
                      >
                        <FaTrash />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <EmptyMessage>Nema promo kodova</EmptyMessage>
              )}
            </DataTable>
          </DataTableScroll>

          {pagination && pagination.totalPages > 1 && (
            <PaginationContainer>
              <Button
                $secondary
                $small
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                Prethodna
              </Button>
              <PageInfo>
                Strana {pagination.page} od {pagination.totalPages}
              </PageInfo>
              <Button
                $secondary
                $small
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
              >
                Sledeca
              </Button>
            </PaginationContainer>
          )}
        </>
      )}
    </>
  );
};

export default AdminPromoCodes;
